import cn from 'classnames';
import { FC, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { EncounterAnalytics } from '@/shared/api/transcriptor/transcriptorApi';

import styles from './AnalyticsView.module.scss';
import { groupTagsByGroupCode } from './lib/groupTagsByGroupCode';
import { AnalyticsSectionBlock } from './ui/AnalyticsSectionBlock';
import { AnalyticsTagCard } from './ui/AnalyticsTagCard';

type AnalyticsViewProps = {
  analytics: EncounterAnalytics | null | undefined;
  className?: string;
};

export const AnalyticsView: FC<AnalyticsViewProps> = (props) => {
  const { analytics, className } = props;

  const tagGroups = useMemo(
    () => groupTagsByGroupCode(analytics?.tags?.tags ?? []),
    [analytics],
  );

  const sections = analytics?.sections ?? [];
  const sectionsWithContent = sections.filter((section) => {
    if (section.content.kind === 'list') {
      return (section.content.items?.length ?? 0) > 0;
    }
    return Boolean(section.content.text?.trim());
  });

  const hasTags = tagGroups.length > 0;
  const hasSections = sectionsWithContent.length > 0;

  if (!hasTags && !hasSections) {
    return (
      <div className={cn(styles.empty, className)}>
        <FormattedMessage
          id="encounters.details.analyticsEmpty"
          defaultMessage="No analytics available"
        />
      </div>
    );
  }

  return (
    <div
      className={cn(styles.container, className)}
      data-component="encounter-analytics-view"
    >
      {hasTags && (
        <section className={styles.tagsBlock}>
          <h3 className={styles.sectionHeading}>
            <FormattedMessage
              id="encounters.details.analyticsTags"
              defaultMessage="Tags"
            />
          </h3>
          <div className={styles.tagsGrid}>
            {tagGroups.map((group) => (
              <AnalyticsTagCard key={group.code} group={group} />
            ))}
          </div>
        </section>
      )}

      {sectionsWithContent.map((section) => (
        <AnalyticsSectionBlock key={section.code} section={section} />
      ))}
    </div>
  );
};
